import React, { useId, useMemo } from 'react'
import {
  Controller,
  useFieldArray,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form'
import { FiltersSchema } from '../filtersSchema'
import { Box, Button, Field, Flex, Select, useAppTheme } from '@workwhile/ui'
import { CircleMinus } from 'lucide-react'
import { useShiftsContextValue } from 'pages/Shifts/Shifts/context/ShiftsProvider'
import { indexBy, prop } from 'ramda'

const AddedMarketRow = ({
  remove,
  index,
  value,
  isLast,
}: {
  remove: UseFieldArrayRemove
  index: number
  value: string
  isLast: boolean
}) => {
  const { colors, radii, space } = useAppTheme()
  return (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      gap={space[2]}
      mb={isLast ? 0 : space[2]}
    >
      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
        border="1px solid"
        borderColor={colors.neutrals[100]}
        p={2}
        borderRadius={radii.small}
        width="100%"
        position="relative"
      >
        {value}
      </Flex>
      <Button
        onClick={() => remove(index)}
        variant="text"
        color="red"
        size="small"
        danger
        style={{
          margin: 0,
          height: '1.6rem', // This is to resolve a bug in our UI library where the width and height is being set to `small` (literally) instead of a `px` value
        }}
      >
        <CircleMinus size={16} />
      </Button>
    </Flex>
  )
}

const AddedMarketCodes = () => {
  const baseId = useId()
  const { watch, control } = useFormContext<FiltersSchema>()
  const {
    state: { markets },
  } = useShiftsContextValue()

  const { remove } = useFieldArray({
    name: 'shift.marketCodes',
    control,
  })

  const marketCodes = watch('shift.marketCodes') ?? []

  const marketMap = useMemo(() => {
    if (!markets) return {}
    const groupedMarkets = indexBy(prop('code'), markets)
    return groupedMarkets
  }, [markets])

  const addedMarketRows = marketCodes.map(({ code }, index) => (
    <AddedMarketRow
      key={`added-market-${baseId}-${code}-${index}`}
      remove={remove}
      index={index}
      value={`${marketMap[code]?.name} (${code})`}
      isLast={index === marketCodes.length - 1}
    />
  ))
  return (
    <Box width="256px" maxHeight="360px" overflow="auto">
      {addedMarketRows}
    </Box>
  )
}

export const MarketCodesFilter = () => {
  const { control, trigger, getValues, setValue, resetField } =
    useFormContext<FiltersSchema>()
  const {
    state: { markets },
  } = useShiftsContextValue()

  const marketOptions = useMemo(() => {
    return markets?.map((market) => ({
      label: `${market.name} (${market.code})`,
      value: market.code,
    }))
  }, [markets])

  const { append } = useFieldArray({
    name: 'shift.marketCodes',
    control,
  })

  return (
    <Box>
      <Controller
        control={control}
        name="shift.marketCodeUserInput"
        defaultValue={undefined}
        render={({ field, fieldState: { error, invalid } }) => (
          <Field error={error?.message?.toString()}>
            <Flex gap={2} width="256px" maxHeight="360px">
              <Select
                block
                {...field}
                options={marketOptions}
                isClearable
                isSearchable
                value={
                  marketOptions?.find(
                    (option) => option.value === field.value
                  ) || null
                }
                onChange={(option) => {
                  field.onChange(option?.value)
                }}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: '100%',
                  }),
                }}
              />
              <Button
                variant="primary"
                disabled={invalid}
                onClick={async () => {
                  const isInputValid = await trigger(
                    'shift.marketCodeUserInput'
                  )
                  const marketCode = getValues('shift.marketCodeUserInput')
                  if (!marketCode) return

                  const addedMarkets = getValues('shift.marketCodes') ?? []
                  const doesInputExistInCurrentGroup = addedMarkets.some(
                    (market) => market.code === marketCode
                  )

                  const isValid = isInputValid && !doesInputExistInCurrentGroup

                  if (isValid) {
                    append({ code: marketCode })
                    resetField('shift.marketCodeUserInput', {
                      defaultValue: undefined,
                    })
                    setValue('shift.marketCodeUserInput', undefined)
                  }

                  if (doesInputExistInCurrentGroup) {
                    resetField('shift.marketCodeUserInput', {
                      defaultValue: undefined,
                    })
                  }
                }}
              >
                Add
              </Button>
            </Flex>
          </Field>
        )}
      />

      <AddedMarketCodes />
    </Box>
  )
}
